<script>
import {
  iButton,
  iButtonSizes,
  iButtonStyles,
  iButtonTypes,
  iFilter,
  iIcon,
  iSelect,
  iTextInput,
  iTooltip,
  iTooltipAlignmentMode,
  iStepper,
} from '@inscribe/signal';
import { mapGetters } from 'vuex';
import RuleEngine from '@/utils/ruleEngine';
import { operatorMap, resolveConditionValue } from '@/utils/conditions';
import { DOCUMENT_TYPE, FILE_TYPE } from '@/constants/requirementVariables';

export default {
  components: {
    iButton,
    iFilter,
    iIcon,
    iSelect,
    iStepper,
    iTextInput,
    iTooltip,
  },
  props: {
    rule: {
      type: Object,
      default: null,
    },
    itemIndex: {
      type: Number,
      default: null,
    },
    forceValue: { type: Boolean, default: false },
    editable: { type: Boolean, default: true },
  },
  data() {
    return {
      currentRule: {},
      iButtonSizes,
      iButtonStyles,
      iButtonTypes,
      iTooltipAlignmentMode,
      rulesSchema: null,
      selectedVariable: null,
      selectedOperator: null,
      newText: '',
      ruleId: crypto.randomUUID(),
      valuesFilterOpen: false,
    };
  },

  computed: {
    filterText() {
      if (!this.currentRule?.value || !this.currentRule.value?.length) {
        return 'Select a value';
      } else {
        const maxValues = 2;
        const selectedString = this.currentRule?.value
          .slice(0, maxValues)
          .map((value) =>
            resolveConditionValue(this.selectedVariable?.name, value),
          )
          .join(', ');

        return this.currentRule?.value?.length > maxValues
          ? `${resolveConditionValue(
              this.selectedVariable?.name,
              this.currentRule?.value[0],
            )} and ${this.currentRule?.value.length - 1} more`
          : selectedString;
      }
    },
    nameOptions() {
      return (
        this.rulesSchema?.variables.map((variable) => ({
          ...variable,
          text: variable.label,
          value: variable.name,
        })) || []
      );
    },
    operatorOptions() {
      if (!this.selectedVariable) {
        return [];
      }

      return this.rulesSchema?.variable_type_operators[
        this.selectedVariable.field_type
      ].map((operator) => ({
        ...operator,
        text: operatorMap[operator.name],
        value: operator.name,
      }));
    },
    valueOptions() {
      return this.selectedVariable?.options.map((option) => {
        const label = resolveConditionValue(
          this.selectedVariable?.name,
          option,
        );
        return {
          option,
          label,
        };
      });
    },
    isNumeric() {
      return new RuleEngine(this.rulesSchema).isNumeric(this.selectedVariable);
    },
    isNumericWithMax() {
      return new RuleEngine(this.rulesSchema).isNumericWithMax(
        this.selectedVariable,
      );
    },
    isBoolean() {
      return new RuleEngine(this.rulesSchema).isBoolean(this.selectedVariable);
    },
    isText() {
      return new RuleEngine(this.rulesSchema).isText(this.selectedVariable);
    },
    isMultiSelect() {
      return (
        [DOCUMENT_TYPE, FILE_TYPE].includes(this.selectedVariable?.name) ||
        this.selectedVariable?.field_type === 'select_multiple' ||
        this.selectedVariable?.field_type === 'create_multiple'
      );
    },
  },
  mounted() {
    this.currentRule = this.rule;
    this.rulesSchema = this.getRulesSchema();
    this.reloadRule(true);
  },
  methods: {
    ...mapGetters(['getRulesSchema']),
    reloadRule(isInitialLoad = false) {
      if (this.currentRule) {
        this.onVariableChange(this.currentRule.name, isInitialLoad);
        this.onOperatorChange(this.currentRule.operator, isInitialLoad);

        // multi values
        if (this.isMultiSelect && this.rule?.value?.length) {
          this.currentRule.valueLabels = this.rule.value.map((value) => {
            const match = this.valueOptions.find((optionObj) => {
              return optionObj.option === value;
            });
            return match?.label || null;
          });
        }

        this.newText = this.currentRule.value;
      }
    },
    isRuleValid(rule) {
      let validValue =
        typeof rule.value !== 'undefined' &&
        rule.value !== null &&
        (typeof rule.value !== 'string' || rule.value.trim() !== '');
      if (this.isMultiSelect) {
        let allValuesAreValid = rule.value?.length;
        allValuesAreValid &&
          rule.value?.forEach((value) => {
            if (!this.valueOptions.find((option) => option.option !== value)) {
              allValuesAreValid = false;
            }
          });
        validValue = validValue && rule.value?.length > 0 && allValuesAreValid;
      } else if (this.isNumeric) {
        validValue = validValue && rule.value > -1;
      }
      return !!rule.name && !!rule.operator && validValue;
    },
    onChange() {
      this.$emit('on-rule-change', {
        index: this.itemIndex,
        rule: {
          ...this.currentRule,
          isValid: this.isRuleValid(this.currentRule),
        },
      });
    },
    onDeleteRequirement() {
      this.$emit('on-rule-delete', {
        index: this.itemIndex,
        rule: this.currentRule,
      });
    },
    onVariableChange(value, isInitialLoad = false) {
      if (value !== this.selectedVariable?.name) {
        this.selectedVariable = this.nameOptions.find(
          (option) => option.name === value,
        );
        if (!isInitialLoad && this.operatorOptions.length) {
          this.onOperatorChange(this.operatorOptions[0].name, false);
        }
        this.currentRule.name = value;
        !isInitialLoad && this.resetValue();
        !isInitialLoad && this.onChange();
      }
    },
    onOperatorChange(value, sendEvent = true) {
      this.selectedOperator = this.operatorOptions.find(
        (option) => option.name === value,
      );
      this.currentRule.operator = value;
      sendEvent && this.onChange();
    },
    resetValue() {
      if (this.isNumeric) {
        this.currentRule.value = 1;
      } else if (this.isBoolean) {
        this.currentRule.value = false;
      } else if (this.isText) {
        this.currentRule.value = '';
      } else if (this.isMultiSelect) {
        this.currentRule.value = [];
        this.currentRule.valueLabels = [];
      }
    },
    onValueChange(rawValue) {
      // tags
      let finalValue = rawValue;
      let valueLabels = [];
      if (rawValue.selection) {
        finalValue = rawValue.selection.map((t) => t.option);
        valueLabels = rawValue.selection.map((t) => t.label);
      }

      if (this.currentRule) {
        this.currentRule.value = finalValue;
        this.currentRule.valueLabels = valueLabels;
      }
      this.onChange();
    },
  },
};
</script>

<template>
  <div
    class="decision-engine-rule-setter d-flex flex-column"
    data-testid="decision-engine-rule-setter"
  >
    <div class="line d-flex flex-row i-gap-6">
      <!-- name -->
      <i-select
        :class="{ unset: !selectedVariable }"
        :options="nameOptions"
        :default-value="currentRule.name"
        placeholder="Select a Variable"
        size="md"
        :disabled="!editable || forceValue"
        data-testid="variable-select"
        @option-selected="onVariableChange"
      />

      <!-- operator -->
      <i-select
        :class="{ unset: !selectedOperator }"
        :options="operatorOptions"
        :default-value="currentRule.operator"
        :disabled="!selectedVariable || !editable || forceValue"
        placeholder="Select an operator"
        data-testid="operator-select"
        @option-selected="onOperatorChange"
      />

      <!-- value, can be of these types:
        stepper: numeric, trust_score, quality_score,
        filter: select_multiple,
        text-input: string,  document_text
        nothing: boolean,
       -->
      <div class="value-container d-flex flex-grow-1 align-items-center">
        <!-- numeric -->
        <i-stepper
          v-if="isNumeric"
          :max-value="isNumericWithMax ? 100 : Infinity"
          :value="currentRule.value"
          size="medium"
          :full-width="true"
          :precision="2"
          :enabled="editable"
          data-testid="value-stepper"
          @value-changed="onValueChange"
        />

        <!-- ifilter -->
        <div
          v-else-if="isMultiSelect"
          :id="'filter-wrapper-' + ruleId"
          class="filter-wrapper"
        >
          <i-tooltip
            v-if="!editable"
            :target="'#filter-wrapper-' + ruleId"
            title="Values"
            :description="currentRule.valueLabels.join(', ')"
            :alignment="iTooltipAlignmentMode.TOP_MIDDLE"
          >
          </i-tooltip>
          <div
            :id="'rule-' + ruleId"
            :class="{ 'is-open': valuesFilterOpen, disabled: !editable }"
            class="filter-trigger d-flex flex-grow align-items-center i-px-8 i-py-5 i-gap-6 i-radius-md"
            data-testid="filter-trigger"
          >
            <div
              :class="{
                'i-text-neutral-90': !currentRule.valueLabels?.length,
                'i-text-neutral-120': currentRule.valueLabels?.length,
              }"
              class="filter-value-text d-flex flex-grow-1 i-text-body-2"
            >
              {{ filterText }}
            </div>
            <i-icon
              :symbol="
                valuesFilterOpen ? 'small-chevron-up' : 'small-chevron-down'
              "
              class="i-text-light-90"
              :size="16"
            ></i-icon>
          </div>
          <i-filter
            label="Values"
            title="Select values"
            search-input-placeholder="Search"
            :custom-trigger="'#rule-' + ruleId"
            :options="valueOptions"
            :searchable="true"
            :max-selected-extended-list="2"
            :value="currentRule.valueLabels"
            :width="260"
            :enabled="editable"
            :show-empty-create-button="false"
            data-testid="value-filter"
            @selection-changed="onValueChange"
            @i-filter-open-changed="(value) => (valuesFilterOpen = value)"
          />
        </div>

        <!-- string -->
        <i-text-input
          v-else-if="isText"
          :value="newText"
          placeholder="Type here"
          @input="onValueChange"
        ></i-text-input>
      </div>

      <!-- delete icon -->
      <i-button
        v-if="editable"
        class="link-icon"
        left-icon="small-delete-bin-1"
        :type="iButtonTypes.NEUTRAL"
        :button-style="iButtonStyles.GHOST"
        :size="iButtonSizes.LARGE"
        @button-click="onDeleteRequirement"
      ></i-button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.decision-engine-rule-setter {
  .i-select {
    width: 33%;

    @media screen and (max-width: 1400px) {
      max-width: 282px;
    }

    &.unset {
      :deep(.selected-input) {
        span {
          color: $light-neutral-90;
        }
      }
    }

    :deep(.selected-input) {
      &:not(.disabled) {
        background: $light-neutral-default;
      }
    }
  }

  .i-stepper {
    height: 44px;

    :deep(button) {
      height: 42px;
    }
  }

  .i-text-input {
    width: 100%;

    :deep(.input-wrapper) {
      background: $light-neutral-default;
    }
  }

  .value-container {
    width: 33%;
    .filter-wrapper {
      display: inline;
      height: 44px;
      width: 100%;
      overflow-x: hidden;

      .filter-trigger {
        position: relative;
        text-wrap: nowrap;
        width: 100%;
        height: 100%;
        cursor: pointer;
        border: 1px solid $light-neutral-70;
        background: $light-neutral-default;
        user-select: none;

        &.disabled {
          background: $light-neutral-30;
          pointer-events: none;
        }

        &:after {
          content: '';
          position: absolute;
          border-radius: 8px;
          left: 0;
          right: 0;
          top: 0;
          bottom: 0;
          border: 1px solid transparent;
        }

        &.is-open {
          border: 1px solid $light-primary-90;

          &:after {
            border: 1px solid $light-primary-90;
          }
        }

        .filter-value-text {
          width: 240px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }
    }
  }
}
</style>
